import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

/**
 * HomepageSearchBar — inline search input that lives on the homepage.
 *
 * `docked` (default false) — renders with an elevated shadow, white surface,
 * and slightly larger height so it visually anchors to the bottom of the
 * hero card when placed via a negative-top margin (see Directory.jsx). This
 * gives the "search input floating on the hero's edge" look instead of a
 * disconnected pill.
 *
 * Smart routing rule (same as GlobalHeader's search): natural-language
 * questions → /wikifi so the selected frontier model can answer, keyword-
 * style input stays in the directory.
 */
export function HomepageSearchBar({ docked = false }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    const isQuestion =
      v.includes("?") ||
      v.split(/\s+/).length >= 5 ||
      /^(what|how|why|can|when|where|who|which|is|are|do|does|should|would|could|help|i\s+(am|want|need|would|would\s+like|'m)|looking|create|build|make|find|show|explain|compare|recommend)\b/i.test(v);
    if (isQuestion) nav(`/wikifi?q=${encodeURIComponent(v)}`);
    else nav(`/?search=${encodeURIComponent(v)}`);
  };

  const shell = docked
    ? "flex items-center gap-2 p-2 rounded-xl bg-white border border-[#E7EAF0] shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18),0_2px_6px_-2px_rgba(15,23,42,0.08)]"
    : "flex items-center gap-2 mb-4";

  return (
    <form onSubmit={submit} className={shell} data-testid="homepage-search-form">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A93A6]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="I'm looking for an AI that can help me do budgeting…"
          className={`w-full ${docked ? "h-11" : "h-10"} rounded-md bg-white ${docked ? "border-0 focus:ring-2 focus:ring-[#0B1220]/10" : "border border-[#E7EAF0] focus:border-[#0B1220]/40"} pl-11 pr-4 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none transition-all`}
          data-testid="homepage-search"
        />
      </div>
      <button
        type="submit"
        className={`${docked ? "h-11" : "h-10"} px-5 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold flex items-center gap-1.5 hover:brightness-110 transition-all whitespace-nowrap`}
        data-testid="homepage-search-btn"
      >
        <Search size={12}/> Search
      </button>
    </form>
  );
}
